/**
 * P_WEEKLY_REPORT_CARD_2.3 — Distribution Center tab.
 *
 *   Per-org Recipients table (any role).
 *   Super-admin Distribution Groups table — cross-org executive bundles.
 *
 * Visual philosophy carried from the publication: typography first,
 * no dashboard density. Recipients render as full-width rows with
 * generous spacing, NOT a packed table grid.
 */
import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { weeklyDistributionAPI } from '../../lib/api';

export default function WeeklyReportDistribution({
    organizationId, organizationName, isSuperAdmin, organizations, t,
}) {
    return (
        <section data-testid="weekly-report-distribution">
            <h2 className="text-xs sm:text-sm uppercase tracking-[0.32em] opacity-60 mb-16 sm:mb-20">
                {t('weeklyReport.distribution.title')}
            </h2>

            <RecipientsBlock
                organizationId={organizationId}
                organizationName={organizationName}
                t={t}
            />

            {isSuperAdmin && (
                <GroupsBlock
                    organizations={organizations}
                    t={t}
                />
            )}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Recipients (per-org)
// ─────────────────────────────────────────────────────────────────────
const RecipientsBlock = ({ organizationId, organizationName, t }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');

    const refresh = async () => {
        if (!organizationId) return;
        setLoading(true);
        try {
            const res = await weeklyDistributionAPI.listRecipients(organizationId);
            setItems(res.data?.items || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, [organizationId]);

    const handleAdd = async (e) => {
        e?.preventDefault?.();
        const email = newEmail.trim();
        if (!email) return;
        setBusy(true);
        try {
            await weeklyDistributionAPI.createRecipient({
                organization_id: organizationId,
                email,
                name: newName.trim() || null,
            });
            setNewEmail('');
            setNewName('');
            await refresh();
            toast.success(t('weeklyReport.distribution.recipientAdded'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    const handleToggle = async (row) => {
        setBusy(true);
        try {
            await weeklyDistributionAPI.updateRecipient(row.id, { enabled: !row.enabled });
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(t('weeklyReport.distribution.confirmDelete', { email: row.email }))) return;
        setBusy(true);
        try {
            await weeklyDistributionAPI.deleteRecipient(row.id);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mb-32" data-testid="weekly-report-recipients-block">
            <h3 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-2">
                {organizationName || '—'}
            </h3>
            <p className="text-xs uppercase tracking-[0.24em] opacity-50 mb-12">
                {t('weeklyReport.distribution.recipientsLabel')}
            </p>

            {loading ? (
                <div className="py-16 flex items-center gap-3 opacity-60">
                    <Loader2 className="w-5 h-5 animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <p className="text-base opacity-50 mb-8" data-testid="weekly-report-recipients-empty">
                    {t('weeklyReport.distribution.recipientsEmpty')}
                </p>
            ) : (
                <ul className="space-y-6 mb-12">
                    {items.map((row) => (
                        <li
                            key={row.id}
                            data-testid={`weekly-report-recipient-${row.id}`}
                            className="flex items-center justify-between gap-6 py-3 border-b border-white/5"
                        >
                            <div className="min-w-0">
                                <p className="text-lg sm:text-xl font-light truncate">
                                    <span className={row.enabled ? '' : 'opacity-40'}>{row.email}</span>
                                </p>
                                {row.name && (
                                    <p className="text-sm opacity-50 truncate">{row.name}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => handleToggle(row)}
                                    disabled={busy}
                                    data-testid={`weekly-report-recipient-toggle-${row.id}`}
                                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-full border ${
                                        row.enabled
                                            ? 'border-emerald-300/60 text-emerald-300/90'
                                            : 'border-white/20 text-white/40'
                                    }`}
                                >
                                    {row.enabled
                                        ? t('weeklyReport.distribution.enabled')
                                        : t('weeklyReport.distribution.disabled')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(row)}
                                    disabled={busy}
                                    data-testid={`weekly-report-recipient-delete-${row.id}`}
                                    className="p-2 opacity-50 hover:opacity-100 transition-opacity"
                                    aria-label="Delete recipient"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <form
                onSubmit={handleAdd}
                className="flex flex-col sm:flex-row sm:items-end gap-4"
                data-testid="weekly-report-recipient-add-form"
            >
                <div className="flex-1">
                    <label className="block text-[10px] uppercase tracking-[0.24em] opacity-50 mb-2">
                        {t('weeklyReport.distribution.email')}
                    </label>
                    <input
                        type="email"
                        value={newEmail}
                        placeholder="owner@company.com"
                        onChange={(e) => setNewEmail(e.target.value)}
                        data-testid="weekly-report-recipient-add-email"
                        className="w-full bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
                        required
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-[10px] uppercase tracking-[0.24em] opacity-50 mb-2">
                        {t('weeklyReport.distribution.nameOptional')}
                    </label>
                    <input
                        type="text"
                        value={newName}
                        placeholder=""
                        onChange={(e) => setNewName(e.target.value)}
                        data-testid="weekly-report-recipient-add-name"
                        className="w-full bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy || !newEmail.trim()}
                    data-testid="weekly-report-recipient-add-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
                >
                    <Plus className="w-3 h-3" />
                    {t('weeklyReport.distribution.addRecipient')}
                </button>
            </form>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────
// Distribution groups (super-admin only)
// ─────────────────────────────────────────────────────────────────────
const GroupsBlock = ({ organizations, t }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [recipEmail, setRecipEmail] = useState('');
    const [recipName, setRecipName] = useState('');
    const [selectedOrgs, setSelectedOrgs] = useState([]);

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await weeklyDistributionAPI.listGroups();
            setItems(res.data?.items || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    const toggleOrgSelection = (orgId) => {
        setSelectedOrgs((prev) =>
            prev.includes(orgId) ? prev.filter((x) => x !== orgId) : [...prev, orgId]
        );
    };

    const handleCreate = async (e) => {
        e?.preventDefault?.();
        if (!groupName.trim() || !recipEmail.trim() || selectedOrgs.length === 0) return;
        setBusy(true);
        try {
            await weeklyDistributionAPI.createGroup({
                group_name: groupName.trim(),
                recipient_email: recipEmail.trim(),
                recipient_name: recipName.trim() || null,
                organization_ids: selectedOrgs,
            });
            setGroupName('');
            setRecipEmail('');
            setRecipName('');
            setSelectedOrgs([]);
            await refresh();
            toast.success(t('weeklyReport.distribution.groupCreated'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteGroup = async (row) => {
        if (!window.confirm(t('weeklyReport.distribution.confirmDeleteGroup', { name: row.group_name }))) return;
        setBusy(true);
        try {
            await weeklyDistributionAPI.deleteGroup(row.id);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('weeklyReport.states.error'));
        } finally {
            setBusy(false);
        }
    };

    const orgName = (id) => organizations.find((o) => o.id === id)?.name || id;

    return (
        <div className="pt-20 border-t border-white/10" data-testid="weekly-report-groups-block">
            <h3 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-2">
                {t('weeklyReport.distribution.groupsTitle')}
            </h3>
            <p className="text-xs uppercase tracking-[0.24em] opacity-50 mb-4">
                {t('weeklyReport.distribution.groupsSubtitle')}
            </p>
            <p className="text-sm opacity-60 mb-12 max-w-xl leading-relaxed">
                {t('weeklyReport.distribution.groupsHelper')}
            </p>

            {loading ? (
                <div className="py-12 flex items-center gap-3 opacity-60">
                    <Loader2 className="w-5 h-5 animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <p className="text-base opacity-50 mb-8" data-testid="weekly-report-groups-empty">
                    {t('weeklyReport.distribution.groupsEmpty')}
                </p>
            ) : (
                <ul className="space-y-10 mb-16">
                    {items.map((row) => (
                        <li
                            key={row.id}
                            data-testid={`weekly-report-group-${row.id}`}
                            className="border-b border-white/5 pb-8"
                        >
                            <div className="flex items-start justify-between gap-6 mb-3">
                                <div className="min-w-0">
                                    <p className="text-xl sm:text-2xl font-light">{row.recipient_name || row.recipient_email}</p>
                                    <p className="text-sm opacity-60">{row.recipient_email}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteGroup(row)}
                                    disabled={busy}
                                    data-testid={`weekly-report-group-delete-${row.id}`}
                                    className="p-2 opacity-50 hover:opacity-100 transition-opacity shrink-0"
                                    aria-label="Delete group"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-xs uppercase tracking-[0.22em] opacity-50 mb-2">
                                {t('weeklyReport.distribution.receives')}
                            </p>
                            <ul className="space-y-1">
                                {(row.organization_ids || []).map((oid) => (
                                    <li key={oid} className="text-sm opacity-80">
                                        ✓ {orgName(oid)}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            )}

            {/* Create group form */}
            <form
                onSubmit={handleCreate}
                className="space-y-6"
                data-testid="weekly-report-group-add-form"
            >
                <p className="text-xs uppercase tracking-[0.24em] opacity-60 mb-2">
                    {t('weeklyReport.distribution.newGroup')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <input
                        type="text"
                        value={groupName}
                        placeholder={t('weeklyReport.distribution.groupName')}
                        onChange={(e) => setGroupName(e.target.value)}
                        data-testid="weekly-report-group-add-name"
                        className="bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
                        required
                    />
                    <input
                        type="email"
                        value={recipEmail}
                        placeholder="executive@head-office.com"
                        onChange={(e) => setRecipEmail(e.target.value)}
                        data-testid="weekly-report-group-add-email"
                        className="bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
                        required
                    />
                    <input
                        type="text"
                        value={recipName}
                        placeholder={t('weeklyReport.distribution.recipientNameOptional')}
                        onChange={(e) => setRecipName(e.target.value)}
                        data-testid="weekly-report-group-add-recip-name"
                        className="bg-transparent border-b border-white/20 px-1 py-2 text-base focus:outline-none focus:border-white/60"
                    />
                </div>

                <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] opacity-50 mb-3">
                        {t('weeklyReport.distribution.selectOrganizations')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {organizations.map((o) => {
                            const sel = selectedOrgs.includes(o.id);
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    onClick={() => toggleOrgSelection(o.id)}
                                    data-testid={`weekly-report-group-org-toggle-${o.id}`}
                                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                        sel
                                            ? 'border-emerald-300/70 text-emerald-300/90 bg-emerald-300/10'
                                            : 'border-white/20 text-white/60 hover:border-white/50'
                                    }`}
                                >
                                    {o.name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={busy || !groupName.trim() || !recipEmail.trim() || selectedOrgs.length === 0}
                    data-testid="weekly-report-group-add-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full hover:border-white/80 transition-colors disabled:opacity-40"
                >
                    <Plus className="w-3 h-3" />
                    {t('weeklyReport.distribution.createGroup')}
                </button>
            </form>
        </div>
    );
};
